import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Logo from "../components/Logo";
import UserMenu, { LogInLink } from "../account/UserMenu";
import { useSubscribed } from "../account/useListingAccess";
import { BUY_PROPERTY_REPORT_PATH, BUY_REPORT_PATH, SIGNUP_PATH, SUBSCRIBE_PATH } from "../account/accountRules";
import { LISTING_PATH, SAVED_LISTINGS_PATH } from "../account/listingRules";
import { PROPERTY_PATH } from "../property/propertyValue";
import { selectMainMenuOpen, setMainMenuOpen } from "../features/map/mapSlice";
import { BIG_PICTURE_PATH } from "./landingScroll";
import { AREA_PAGES_PATH } from "../Layers/areaPageLink";
import { INSIGHTS_PATH } from "../insights/insightsLinks";

// The main menu drawer, the map page's own (components/MainMenu.js), loaded when first opened
// so it stays out of the landing page's bundle
const loadMainMenu = () => import("../components/MainMenu");
const MainMenu = lazy(loadMainMenu);

// The map app's own path (Landing.js's MAP_PATH), kept here to avoid an import cycle
const MAP_PATH = '/map';

const startsWith = (path, prefix) => path === prefix || path.startsWith(prefix + '/');

// The top menu of every page framed by .lp (the landing page, property pages, account
// pages and My reports), the same everywhere for each kind of user:
//   logged out:  Open the map, Big Picture, Plans, Buy property report, then Log in and Sign up free
//   logged in:   Open the map, Big Picture, Property lookup, Plans, Buy property report, then the user icon
//   subscribed:  Open the map, Big Picture, Property lookup, My reports, then the user icon
// The option for the page being shown is highlighted. Big Picture is its own page
// (BigPicture.js); Plans is a section of the landing page (/#plans), which Landing.js scrolls to. The menu button at the
// far left before the logo opens the main menu drawer, the same as the map page's, on every
// width; on phones the top options are left to it.
const SiteNav = ({isNarrow}) => {
    const {pathname, hash} = useLocation();
    const dispatch = useDispatch();
    const menuOpen = useSelector(selectMainMenuOpen);
    // Mounted once first opened, then kept for its closing slide
    const [menuLoaded, setMenuLoaded] = useState(menuOpen);
    useEffect(() => { if (menuOpen) setMenuLoaded(true); }, [menuOpen]);
    // A new page or section closes it
    useEffect(() => { dispatch(setMainMenuOpen(false)); }, [pathname, hash, dispatch]);
    const {signedIn, subscribed, waiting} = useSubscribed();

    const options = [
        {key:'map', text:'Open the map', to:MAP_PATH, on:startsWith(pathname, MAP_PATH)},
        {key:'bp', text:'Big Picture', to:BIG_PICTURE_PATH, on:startsWith(pathname, BIG_PICTURE_PATH)},
        // The static area pages, outside the app: a plain link (href)
        {key:'areas', text:'Areas', href:AREA_PAGES_PATH, className:'hide-md'},
        // The insights articles, also static pages outside the app
        {key:'insights', text:'Insights', href:INSIGHTS_PATH, className:'hide-md'},
        signedIn && {key:'prop', text:'Property lookup', to:PROPERTY_PATH, on:startsWith(pathname, PROPERTY_PATH)},
        subscribed && {key:'list', text:'My reports', to:SAVED_LISTINGS_PATH,
            on:startsWith(pathname, SAVED_LISTINGS_PATH) || startsWith(pathname, LISTING_PATH)},
        !subscribed && !waiting && {key:'plans', text:'Plans', to:{pathname:'/', hash:'#plans'},
            on:(pathname === '/' && hash === '#plans') || startsWith(pathname, SUBSCRIBE_PATH)},
        // Left to the drawer where the bar is too narrow for it
        !subscribed && !waiting && {key:'buy', text:'Buy property report', to:BUY_PROPERTY_REPORT_PATH, className:'hide-md',
            on:startsWith(pathname, BUY_PROPERTY_REPORT_PATH) || startsWith(pathname, BUY_REPORT_PATH)},
    ].filter(Boolean);

    return (
        <nav className="nav"><div className="wrap">
            <div className="brand">
                <button type="button" className="menu" aria-label="Menu" aria-expanded={menuOpen} aria-haspopup="dialog"
                    onPointerEnter={loadMainMenu} onFocus={loadMainMenu}
                    onClick={() => dispatch(setMainMenuOpen(!menuOpen))}>
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.8"/></svg>
                </button>
                <Logo isMobile={isNarrow}/>
            </div>
            <div className="links">
                {options.map((o) => o.href ? (
                    <a key={o.key} href={o.href} className={['hide-sm', o.className].filter(Boolean).join(' ')}>{o.text}</a>
                ) : (
                    <Link key={o.key} to={o.to} className={['hide-sm', o.className, o.on && 'on'].filter(Boolean).join(' ')}
                        aria-current={o.on ? 'page' : undefined}>{o.text}</Link>
                ))}
                <UserMenu signedOut={<><LogInLink/><Link className="cta" to={SIGNUP_PATH}>Sign up<span className="cta-free"> free</span></Link><LogInLink icon/></>}/>
            </div>
            {menuLoaded && <Suspense fallback={null}><MainMenu compact={isNarrow}/></Suspense>}
        </div></nav>
    );
};

export default SiteNav;
